import { useCallback, FC } from "react";
import { FileUploadContext } from "@src/lib/contexts/FileUploadContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";
import useFileUploadContextState from "../../libraryItem/hooks/useFileUploadContextState";
import { gql } from "@apollo/client";
import { useUploadScormDialog_CreateScormCourseMutation } from "./UploadSCORMDialog.generated";
import ScormUploadButton from "../../libraryItem/scorm-course/upload/ScormUploadButton";
import { useRouter } from "next/router";
import { useToast } from "@src/lib/hooks/useToast";

type Props = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

const UploadScormDialogContainer: FC<Props> = ({ open, onOpenChange }) => {
  const fileUploadContextState = useFileUploadContextState();
  return (
    <FileUploadContext.Provider value={fileUploadContextState}>
      <UploadSCORMDialog open={open} onOpenChange={onOpenChange} />
    </FileUploadContext.Provider>
  );
};

const UploadSCORMDialog: FC<Props> = ({ open, onOpenChange }) => {
  const router = useRouter();
  const [createScormCourse] = useUploadScormDialog_CreateScormCourseMutation();
  const { addErrorToast } = useToast();

  const onUploadComplete = useCallback(
    async (s3Key: string, packageName: string) => {
      try {
        const response = await createScormCourse({
          variables: { s3Key, packageName },
        });
        onOpenChange?.(false);
        if (response.data?.createSCORMCourseLibraryItem.success) {
          const libraryItemId =
            response.data.createSCORMCourseLibraryItem.scormCourse?.libraryItem
              ?.id;
          if (libraryItemId) {
            router.push({
              pathname: "/library/library-item/[id]",
              query: { id: libraryItemId },
            });
          }
        } else if (response.data?.createSCORMCourseLibraryItem.error) {
          addErrorToast({
            title: "Failed to upload SCORM course. Please try again.",
          });
        }
      } catch (error) {
        addErrorToast({
          title: "Failed to upload SCORM course. Please try again.",
        });
      }
    },
    [onOpenChange, createScormCourse, router, addErrorToast],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-[300px]">
        <DialogHeader>
          <DialogTitle>Upload SCORM Package</DialogTitle>
        </DialogHeader>
        <div className="flex justify-center">
          <ScormUploadButton onUploadComplete={onUploadComplete} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

gql`
  mutation UploadSCORMDialog_CreateSCORMCourse(
    $s3Key: String!
    $packageName: String!
  ) {
    createSCORMCourseLibraryItem(s3Key: $s3Key, packageName: $packageName) {
      success
      error {
        code
      }
      scormCourse {
        libraryItem {
          id
        }
      }
    }
  }
`;

export default UploadScormDialogContainer;
